/**
 * Eigenbau: Haarlinien-Zeile — 65 Stellen im Entwurf, Tremors Table passt nicht.
 *
 * Der Entwurf listet mit Zeilen, nicht mit Karten. Eine Zeile ist eine feste
 * Höhe (52px in Listen, 44px in Nebenspalten), eine Haarlinie unten in
 * `--linie`, und beim Überfahren die Wischfarbe. Kein Rahmen, keine Rundung,
 * kein Schatten.
 *
 * Warum nicht Tremors `Table`: Der Entwurf hat hier kein Spaltenraster. Die
 * Breiten sind je Liste anders belegt, viele Zeilen haben zweizeilige Inhalte,
 * und es gibt keinen Kopf, unter dem sich Spalten ausrichten. Ein `<table>`
 * würde Vorleseprogrammen eine Navigation ankündigen, die es nicht gibt. Wo
 * der Entwurf wirklich eine Tabelle zeigt, bleibt `Table` aus dem Bestand.
 *
 * ── Warum Klassen und kein Wrapper ────────────────────────────────────────
 *
 * `zeileKlassen()` gibt die Klassenliste heraus, statt ein `<div>` um den
 * Inhalt zu legen. Paket 19 verlangt das ausdrücklich:
 *
 * > Die Auftragszeile ist **ein** Bedienelement, kein Div mit `role="button"`
 * > und daneben ein zweiter Knopf. Genau dieser Fehler steckte in der
 * > Pipeline-Karte.
 *
 * So kann eine klickbare Zeile ein echtes `<Link>` oder `<button>` **sein** —
 * mit Tastaturfokus, Kontextmenü und Mittelklick, die ein `div` mit `onClick`
 * alle nicht hat. Für unbewegliche Zeilen gibt es `Zeile` darunter.
 */

import type { ReactNode } from "react";
import { cx, focusRing } from "@/lib/utils";

const HOEHEN = {
  /** Nebenspalten, Projektlisten. */
  44: "h-11",
  /** Die Vorgabe des Entwurfs für Hauptlisten. */
  52: "h-[52px]",
  /** Zweizeilige Zeilen — Ziel oben, Herkunft darunter. */
  auto: "min-h-[52px] py-3",
} as const;

export function zeileKlassen({
  hoehe = 52,
  klickbar = false,
  className,
}: {
  hoehe?: keyof typeof HOEHEN;
  klickbar?: boolean;
  className?: string;
} = {}) {
  return cx(
    "flex w-full items-center gap-4 border-b border-linie text-left",
    HOEHEN[hoehe],
    klickbar && [
      "cursor-pointer transition-colors duration-150 ease-aus hover:bg-wisch",
      focusRing,
    ],
    className,
  );
}

export function Zeile({
  children,
  hoehe = 52,
  className,
}: {
  children: ReactNode;
  hoehe?: keyof typeof HOEHEN;
  className?: string;
}) {
  return <div className={zeileKlassen({ hoehe, className })}>{children}</div>;
}

/**
 * Die Kennungsspalte am Zeilenanfang.
 *
 * Feste Breite, damit die Titel danach auf einer Kante beginnen — sonst
 * wandert der Titelanfang mit der Länge der Kennung, und die Liste sieht
 * ausgefranst aus. Der Entwurf setzt 66px für Aufgabenkennungen und 34px für
 * Projektkürzel.
 */
export function Kennung({
  children,
  breite = 66,
}: {
  children: ReactNode;
  breite?: 34 | 66;
}) {
  return (
    <span
      className={cx(
        "flex-none font-mono text-[11.5px] tabular-nums text-tinte4",
        breite === 66 ? "w-[66px]" : "w-[34px]",
      )}
    >
      {children}
    </span>
  );
}

/**
 * Der Titel in der Mitte: nimmt den Platz, der übrig ist, und kürzt.
 *
 * `min-w-0` ist hier keine Feinheit, sondern Bedingung — ohne die Angabe
 * weigert sich ein Flex-Kind zu schrumpfen und schiebt die Spalten rechts aus
 * der Zeile. Der vollständige Text steht im `title`, damit Gekürztes lesbar
 * bleibt.
 */
export function Zeilentitel({
  children,
  titel,
  leise = false,
}: {
  children: ReactNode;
  titel?: string;
  leise?: boolean;
}) {
  /*
   * `text-tinte` steht hier ausdrücklich.
   *
   * Die Grundregel in `styles.css` färbt jeden Verweis mit dem Akzent — so
   * schreibt es der Entwurf in seinem Kopfbereich. Dort ist das folgenlos,
   * weil er Zeilen als `div` mit `onClick` zeichnet. Hier sind es echte
   * `<a>`, damit Mittelklick, Tastatur und Vorleseprogramme funktionieren —
   * und damit erbte der Titel das Grün, obwohl der Entwurf ihm gar keine
   * Farbe gibt, also Tinte.
   *
   * Der Kommentar an der Grundregel sagt genau das: gefärbt wird nur, was
   * sich selbst keine Farbe gibt.
   */
  return (
    <span
      title={titel}
      className={cx(
        "min-w-0 flex-1 truncate text-tinte",
        leise ? "text-[14px] font-light" : "text-[14.5px] font-normal",
      )}
    >
      {children}
    </span>
  );
}
